import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Activity, Plus, Search } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState } from '@vnetwork/ui/data';
import { Button, Input, Skeleton, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWatchpoints,
  useToggleWatchpoint,
  useDeleteWatchpoint,
  type Watchpoint,
  type WatchpointType,
} from '@/api/healthcheck';
import { WATCHPOINT_TYPES, targetSummary } from '../shared';
import { useWatchpointColumns } from './watchpoint-columns';
import { WatchpointEditDialog } from './WatchpointEditDialog';

export function WatchpointList() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const base = `/services/healthcheck/${projectId}`;
  const { show } = useToast();

  const [type, setType] = useState<WatchpointType | 'all'>('all');
  const [query, setQuery] = useState('');
  const { data, isLoading, isError, refetch } = useWatchpoints(
    projectId,
    type === 'all' ? {} : { type },
  );
  const allMonitors = data?.items ?? [];
  const q = query.trim().toLowerCase();
  const monitors = q
    ? allMonitors.filter((m) => m.name.toLowerCase().includes(q) || targetSummary(m.type, m.config).toLowerCase().includes(q))
    : allMonitors;

  const toggle = useToggleWatchpoint(projectId ?? '');
  const del = useDeleteWatchpoint(projectId ?? '');
  const [pendingDelete, setPendingDelete] = useState<Watchpoint | null>(null);
  const [editTarget, setEditTarget] = useState<Watchpoint | null>(null);

  const columns = useWatchpointColumns({
    busyId: toggle.isPending ? toggle.variables?.id : null,
    onToggle: (wp) =>
      toggle.mutate(
        { id: wp.id, is_active: !wp.is_active },
        {
          onError: (e) => show({ tone: 'danger', title: (e as Error)?.message || 'Update failed' }),
        },
      ),
    onEdit: (wp) => setEditTarget(wp),
    onDelete: (wp) => setPendingDelete(wp),
  });

  const confirmDelete = () => {
    if (!pendingDelete) return;
    const wp = pendingDelete;
    del.mutate(wp.id, {
      onSuccess: () => {
        show({ tone: 'success', title: `Deleted "${wp.name}"` });
        setPendingDelete(null);
      },
      onError: (e) => {
        show({ tone: 'danger', title: (e as Error)?.message || 'Delete failed' });
        setPendingDelete(null);
      },
    });
  };

  return (
    <Container size="xl">
      <PageHeader
        icon={<Activity size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={base}>Monitoring</Link></BreadcrumbItem>
            <BreadcrumbItem current>Monitors</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Monitors"
        description="Watchpoints tracking uptime, latency and certificate health."
        actions={
          <Button size="sm" startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/monitors/create`)}>
            New monitor
          </Button>
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Couldn't load monitors"
          actions={
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          The monitoring service is temporarily unavailable. Please try again.
        </Alert>
      ) : isLoading ? (
        <Skeleton style={{ height: 280 }} />
      ) : (
        <>
          <Toolbar
            start={
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
                <Input
                  placeholder="Search monitors…"
                  startSlot={<Search />}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{ flex: '1 1 160px', maxWidth: 320 }}
                />
                <Select value={type} onValueChange={(v) => setType(v as WatchpointType | 'all')}>
                  <SelectTrigger style={{ minWidth: 160, maxWidth: '100%' }}>
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {(Object.keys(WATCHPOINT_TYPES) as WatchpointType[]).map((t) => (
                      <SelectItem key={t} value={t}>
                        {WATCHPOINT_TYPES[t].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            }
          />
          <DataTable
            columns={columns}
            data={monitors}
            getRowId={(m) => m.id}
            onRowClick={(m) => navigate(`${base}/monitors/${m.id}`)}
            emptyState={
              allMonitors.length === 0 ? (
                <EmptyState
                  icon={<Activity size={28} />}
                  title="No monitors"
                  description="Create your first watchpoint to start tracking uptime and latency."
                  actions={
                    <Button size="sm" startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/monitors/create`)}>
                      New monitor
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<Activity size={28} />}
                  title="No matching monitors"
                  description={q ? 'Try a different search term or type filter.' : 'No monitors match this type filter.'}
                />
              )
            }
            footer={monitors.length ? `Showing ${monitors.length} monitor${monitors.length === 1 ? '' : 's'}` : undefined}
          />
        </>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        destructive
        title="Delete monitor?"
        description={pendingDelete ? `"${pendingDelete.name}" and its check history will be removed. This cannot be undone.` : ''}
        confirmLabel="Delete"
        loading={del.isPending}
        requireText={pendingDelete?.name}
        onConfirm={confirmDelete}
      />

      <WatchpointEditDialog
        watchpoint={editTarget}
        projectId={projectId ?? ''}
        onClose={() => setEditTarget(null)}
      />
    </Container>
  );
}

